import { useEffect, useRef, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@coval/ui"

import {
  notebookRecurrenceSchema,
  type NotebookRecurrence,
} from "@/utils/notebook-recurrence"

import {
  deleteReportSchedule,
  updateReportSchedule,
} from "../utils/generatedNotebooks"
import {
  DEFAULT_RECURRENCE,
  deleteGenerativeSchedule,
  generativeScheduleRequest,
} from "../utils/generativeSchedule"
import { getNotebook } from "../utils/notebooksApi"
import type { NotebookRow } from "../utils/notebookSeries"
import type { Cadence } from "../utils/reportTypes"
import { NotebookRepeatFields } from "./NotebookRepeatFields"

export function NotebookListScheduleDialog({
  open,
  row,
  organizationSlug,
  workspaceId,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  row: NotebookRow | null
  organizationSlug: string
  workspaceId: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const [recurrence, setRecurrence] =
    useState<NotebookRecurrence>(DEFAULT_RECURRENCE)
  const [instructions, setInstructions] = useState("")
  const [cadence, setCadence] = useState<Cadence>("weekly")
  const [enabled, setEnabled] = useState(true)
  const [hasSchedule, setHasSchedule] = useState(false)
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | null>(
    null,
  )
  const [notebookUpdatedAt, setNotebookUpdatedAt] = useState<string | null>(
    null,
  )
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [schedulingAvailable, setSchedulingAvailable] = useState(true)
  const isChatTarget =
    row?.kind === "chat" ||
    (row?.kind === "series" && row.seriesStorage === "chat")
  const targetNotebookId =
    row?.kind === "series"
      ? (row.template?.id ??
        row.schedule?.template_notebook_id ??
        row.editions?.[0]?.id ??
        null)
      : (row?.id ?? null)
  const initialRowRef = useRef(row)
  initialRowRef.current = row

  useEffect(() => {
    if (!open || !initialRowRef.current) {
      return
    }
    const currentRow = initialRowRef.current
    setError(null)
    setSchedulingAvailable(true)
    if (
      currentRow.kind === "generated" ||
      (currentRow.kind === "series" && currentRow.seriesStorage !== "chat")
    ) {
      const schedule = currentRow.schedule
      setCadence(schedule?.cadence ?? "weekly")
      setEnabled(schedule?.enabled ?? true)
      setHasSchedule(Boolean(schedule))
      setExpectedUpdatedAt(schedule?.updated_at ?? null)
      const manual =
        schedule?.execution_mode === "manual" ||
        schedule?.status === "manual_only" ||
        currentRow.schedulingAvailable === false
      setSchedulingAvailable(!manual)
      if (!schedule) {
        setError(
          "This notebook has no schedule. Open the notebook to schedule it.",
        )
      } else if (manual) {
        setError("Automatic notebook runs are unavailable here.")
      }
      setLoading(false)
      return
    }
    if (!targetNotebookId) {
      setError("Open the template notebook to manage this schedule.")
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    void Promise.all([
      getNotebook(
        organizationSlug,
        workspaceId,
        targetNotebookId,
        controller.signal,
      ),
      generativeScheduleRequest(
        { notebookId: targetNotebookId, organizationSlug, workspaceId },
        undefined,
        controller.signal,
      ),
    ])
      .then(([notebook, data]) => {
        if (controller.signal.aborted) {
          return
        }
        setNotebookUpdatedAt(notebook.updated_at)
        setSchedulingAvailable(data.scheduling_available)
        setHasSchedule(Boolean(data.schedule))
        setEnabled(data.schedule?.enabled ?? true)
        setExpectedUpdatedAt(data.schedule?.updated_at ?? null)
        setRecurrence(data.schedule?.recurrence ?? DEFAULT_RECURRENCE)
        setInstructions(data.schedule?.instructions ?? "")
        if (!data.scheduling_available) {
          setError("Automatic notebook runs are unavailable here.")
        }
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            failure instanceof Error
              ? failure.message
              : "Could not load schedule.",
          )
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })
    return () => controller.abort()
  }, [open, organizationSlug, workspaceId, targetNotebookId])

  const recurrenceValidation = notebookRecurrenceSchema.safeParse(recurrence)
  const invalidFields = new Set(
    recurrenceValidation.success
      ? []
      : recurrenceValidation.error.issues.map((issue) => issue.path[0]),
  )

  const saveSchedule = async (nextEnabled: boolean) => {
    if (!row || saving) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (isChatTarget) {
        if (!targetNotebookId || !notebookUpdatedAt) {
          throw new Error("Could not resolve the template notebook.")
        }
        await generativeScheduleRequest(
          { notebookId: targetNotebookId, organizationSlug, workspaceId },
          {
            recurrence,
            instructions,
            enabled: nextEnabled,
            expected_updated_at: expectedUpdatedAt,
            expected_notebook_updated_at: notebookUpdatedAt,
          },
        )
      } else if (row.schedule) {
        await updateReportSchedule(
          organizationSlug,
          workspaceId,
          row.schedule.id,
          {
            enabled: nextEnabled,
            cadence,
            expected_updated_at: expectedUpdatedAt,
          },
        )
      }
      onSaved()
      onOpenChange(false)
    } catch (failure: unknown) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Could not update schedule.",
      )
    } finally {
      setSaving(false)
    }
  }

  const removeSchedule = async () => {
    if (!row || saving) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (isChatTarget) {
        if (!targetNotebookId) {
          throw new Error("Could not resolve the template notebook.")
        }
        await deleteGenerativeSchedule({
          notebookId: targetNotebookId,
          organizationSlug,
          workspaceId,
        })
      } else if (row.schedule) {
        await deleteReportSchedule(
          organizationSlug,
          workspaceId,
          row.schedule.id,
        )
      }
      onSaved()
      onOpenChange(false)
    } catch (failure: unknown) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Could not remove schedule.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {row ? `Schedule · ${row.title}` : "Schedule notebook"}
          </DialogTitle>
          <DialogDescription>
            Update how often Sofia generates a new edition, pause automatic
            runs, or remove the schedule without creating a duplicate notebook.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {loading ? (
            <p role="status" className="text-sm text-coval-text-muted">
              Loading schedule…
            </p>
          ) : isChatTarget ? (
            <>
              <NotebookRepeatFields
                value={recurrence}
                onChange={setRecurrence}
                disabled={saving || !schedulingAvailable}
                errors={{
                  interval: invalidFields.has("interval")
                    ? "Enter a whole number from 1 to 365."
                    : undefined,
                  time: invalidFields.has("time")
                    ? "Choose a valid 24-hour time."
                    : undefined,
                  timezone: invalidFields.has("timezone")
                    ? "Enter a valid IANA timezone."
                    : undefined,
                }}
              />
              <FormField
                label="Instructions for future editions"
                description="Optional. Keep specific sections, change the lookback, or focus on a particular trend."
              >
                {(ids) => (
                  <Textarea
                    {...ids}
                    value={instructions}
                    maxLength={4000}
                    disabled={saving || !schedulingAvailable}
                    onChange={(event) => setInstructions(event.target.value)}
                  />
                )}
              </FormField>
            </>
          ) : (
            <FormField label="Repeat cadence">
              {(ids) => (
                <Select
                  value={cadence}
                  disabled={saving || !schedulingAvailable}
                  onValueChange={(next) => setCadence(next as Cadence)}
                >
                  <SelectTrigger {...ids} aria-label="Repeat cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </FormField>
          )}
          {error ? (
            <p role="alert" className="text-sm text-coval-danger-text">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {hasSchedule && (
            <Button
              variant="secondary"
              disabled={saving || loading}
              onClick={() => void removeSchedule()}
            >
              Remove schedule
            </Button>
          )}
          {hasSchedule && enabled && schedulingAvailable && (
            <Button
              variant="secondary"
              disabled={saving || loading}
              onClick={() => void saveSchedule(false)}
            >
              Pause
            </Button>
          )}
          <Button
            disabled={
              saving ||
              loading ||
              !schedulingAvailable ||
              (isChatTarget && !recurrenceValidation.success) ||
              (!isChatTarget && !hasSchedule)
            }
            loading={saving}
            onClick={() => void saveSchedule(true)}
          >
            {hasSchedule && enabled ? "Save schedule" : "Enable schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
